<template>
    <div class="ReportsPane">
        <div class="container-fluid container-fill">
            <div class="row">
                <div class="col-md-4">
                    <div class="paper">
                        <h4>QA Reports</h4>
                        <ReportDownloader report-label="Missing Provider Data"
                                          endpoint="MentalHealth/Billing/Reports/MissingProviderData"
                                          :parameter-schema="[]"
                                          report-description="Generates two lists:  A list of Providers in Avatar that have no matching provider in OLIVER, and a list of Provider Welligent IDs from Progress Notes that have no Employee Record match in OLIVER." />
                        <ReportDownloader report-label="Missing Client Keys"
                                          endpoint="MentalHealth/Billing/Reports/GetMissingClientKeys"
                                          :parameter-schema="[]"
                                          report-description="Provides a list of clients who do not yet have a Welligent ID or Welligent Program Round in OLIVER." />
                        <ReportDownloader report-label="Unmatched DRAFT Claims in Avatar"
                                          endpoint="MentalHealth/Billing/Reports/AvatarUnmatchedDraftEntries"
                                          :parameter-schema="ParamsCsvUploadAvatarDraftCheck"
                                          method="POST"
                                          report-description="Provides a report of all DRAFT Avatar Claims that don't have a matching Welligent Progress Note.  This report can not check for matching providers between Avatar and Welligent.  This report should always show zero items.  If there are items, then there was either a data entry error, or systems haven't been synced yet.  Correct any issues in this report, and re-run until it shows zero items before finalizing Avatar Sets." />
                        <ReportDownloader report-label="Unmatched FINALIZED Claims in Avatar"
                                          endpoint="MentalHealth/Billing/Reports/UnmatchedBatchedClaims"
                                          :parameter-schema="ParamsStartEnd"
                                          report-description="Provides a report of all Avatar Claims that have received a batch number and can not be matched with a Welligent Progress Note.  Some items will appear here if systems are not synced, others are due to legitimate data entry errors and can not be removed." />
                        <ReportDownloader report-label="STRTP Authorizations Status"
                                          endpoint="MentalHealth/Billing/Reports/GetAuthorizationStatusReport"
                                          :parameter-schema="ParamsSubProgramID"
                                          report-description="Provides report of all current STRTP Clients, and their Authorization Status.  Indicates upcoming Expirations, and Pending." />
                        <ReportDownloader report-label="Expected Service Locations"
                                          endpoint="MentalHealth/Billing/Reports/ExpectedServiceLocationsReport"
                                          :parameter-schema="ParamsTargetDateSubProgramIDs"
                                          report-description="Lists the clients enrolled in the selected programs on the target date, along with their expected Service Locations.  Wraparound = First Team Assignment (Desert vs Non-Desert).  Residential = House Assignment on Target Date.  Outpatient = Outpatient Always." />
                        <ReportDownloader report-label="Misaligned Service Locations"
                                          endpoint="MentalHealth/Billing/Reports/MisalignedServiceLocationsReport"
                                          :parameter-schema="ParamsLocationMultiSelectStartEnd"
                                          report-description="Lists the Welligent Notes that have an Incorrect Service Location Selected.  NOTE: Data from this report is updated form Welligent Data every 2 hours." />
                        <ReportDownloader report-label="Client Master Eligibility"
                                          endpoint="MentalHealth/Billing/Reports/ClientEligibilityReport"
                                          :parameter-schema="ParamsStartEnd"
                                          report-description="Lists all clients enrolled during the timke range, and our ability to submit claims for them." />
                        <ReportDownloader report-label="Cal AIMS Code Errors"
                                          endpoint="MentalHealth/Billing/Reports/IncorrectCalAimsCodes"
                                          :parameter-schema="ParamsStartEndNotify"
                                          report-description="Lists any unbilled notes that have incorrect codes or total time." />
                        <ReportDownloader report-label="Provider Billing Roles"
                                          endpoint="MentalHealth/Billing/Reports/ProviderBillingRoles"
                                          :parameter-schema="ParamsStartEnd"
                                          report-description="Lists all Billing Roles for Clients in Time Range." />

                    </div>
                </div>
                <div class="col-md-4">
                    <div class="paper">
                        <h4>Financial Reports</h4>
                        <ReportDownloader report-label="ELMR Claims File"
                                          endpoint="MentalHealth/Billing/Claims/GenerateClaimsFiles"
                                          :parameter-schema="ParamsClaimsFiles"
                                          method="POST"
                                          report-description="Generates a file that can be uploaded into ELMR to transmit MH Claims" />
                        <ReportDownloader report-label="Latest Cached Claims File"
                                          endpoint="MentalHealth/Billing/Claims/LatestCachedClaimFile"
                                          :parameter-schema="[]"
                                          method="POST"
                                          report-description="Downloads the latest ELMR Claims File for quick retrieval.  File is updated every 24 hours.  File name will indicate the date and time the file was generated." />
                        <ReportDownloader report-label="Avatar/OLIVER Claims Reconciliation"
                                          endpoint="MentalHealth/Billing/Reports/RiversideClaimsRecon"
                                          :parameter-schema="ParamsStartEnd"
                                          method="GET"
                                          report-description="Lists claims in avatar and oliver, with computed composite keys for comparison" />
                        <ReportDownloader report-label="Transfer Welligent Psychiatry Notes"
                                          endpoint="MentalHealth/Billing/Claims/BuildPsychiatryNotesFromWelligentNotes"
                                          :parameter-schema="ParamsCycleCount"
                                          method="GET"
                                          report-description="Transfers Welligent Psychiatry Notes into OLIVER from Gathered Billing Cycle. Warning: Multiple successful runs will result in duplicate notes in OLIVER" />
                        <ReportDownloader report-label="Monthly Revenue per Client"
                                          endpoint="MentalHealth/Billing/Reports/MonthlyRevenuePerClient"
                                          :parameter-schema="ParamsStartEnd"
                                          method="GET"
                                          report-description="Lists amounts in Avatar by month, by client." />
                        <ReportDownloader report-label="Census Approval History"
                                          endpoint="MentalHealth/Billing/Reports/CensusApprovalHistoryReport"
                                          :parameter-schema="ParamsCensusApprovalHistory"
                                          method="GET"
                                          report-description="Lists Census Approval records within the specified date range. Optionally filter by Sub Program or Core Program." />
                        <ReportDownloader report-label="Download Master Rates Sheet"
                                          endpoint="Core/Reports/ProgramReports/ProgramServiceRevenueReport"
                                          :parameter-schema="ProgramServiceRevenueReportParams"
                                          report-description="Generates an Excel file listing all Program Services (grouped by Core Program and Sub Program) with their revenue model details effective on the specified date." />
                    </div>
                </div>
                <div class="col-md-4">
                    <div class="paper">
                        <h4>Productivity Reports</h4>
                        <ReportDownloader report-label="Provider MH Productivity"
                                          endpoint="MentalHealth/Billing/Reports/GetProviderHoursAndOnTime"
                                          :parameter-schema="ParamsStartEndLocation"
                                          report-description="Provides a list of notes for each provider, with their completion date, due date, and whether or not the note was written on time.  Summary report split by assigned supervisor" />
                        <ReportDownloader report-label="MH Weekly Productivity Breakdown"
                                          endpoint="MentalHealth/Billing/Reports/WeeklyProductivityReport"
                                          :parameter-schema="ParamsStartEnd"
                                          report-description="Report showing hours billed, percent to goal, and percent on time.  Info broken down by week, and anggregated for the whole period ran." />
                        
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    //import components
    import ReportDownloader from '../../Core/UI/ReportDownloader.vue';

    //import components
    import { library } from '@fortawesome/fontawesome-svg-core';
    import { faChartBar } from '@fortawesome/free-solid-svg-icons';
    library.add(faChartBar);

    export default {
        props: [],
        data: function () {
            return {
                ProgramServiceRevenueReportParams: [
                    {
                        Label: "Target Date",
                        Name: "TargetDate",
                        Type: "Date",
                        Required: true
                    }
                ],
                ParamsCsvUploadAvatarDraftCheck: [
                    {
                        Label: "pvd 2004 provider services data entry detail csv file:",
                        Name: "FormFile",
                        Type: "FileUpload",
                        Accept: ".csv",
                        Required: true
                    },
                ],
                ParamsClaimsFiles: [
                    {
                        Label: "Start Date:",
                        Name: "StartDate",
                        Type: "Date",
                        Required: true
                    },
                    {
                        Label: "End Date:",
                        Name: "EndDate",
                        Type: "Date",
                        Required: true
                    },
                    {
                        Label: "Invoice Month:",
                        Name: "InvoiceMonth",
                        Type: "Number",
                        Required: true
                    },
                    {
                        Label: "Invoice Year:",
                        Name: "InvoiceYear",
                        Type: "Number",
                        Required: true
                    },
                    {
                        Label: "Invoice Submission Count (always 2 digits):",
                        Name: "CycleNumberString",
                        Type: "String",
                        Required: true
                    },
                    {
                        Label: "Import Past Validation Report:",
                        Name: "PastValidationReport",
                        Type: "FileUpload",
                        Accept: ".xlsx",
                        Required: false
                    },
                    {
                        Label: "File Type",
                        Name: "FileType",
                        Type: "Select",
                        Required: true,
                        Options: [
                            {
                                text: "Billing",
                                value: "Billing"
                            },
                            {
                                text: "Reporting",
                                value: "Reporting"
                            }
                        ]
                    },
                ],
                ParamsStartEnd: [
                    {
                        Label: "Start Date:",
                        Name: "StartDate",
                        Type: "Date",
                        Required: true
                    },
                    {
                        Label: "End Date:",
                        Name: "EndDate",
                        Type: "Date",
                        Required: true
                    }
                ],
                ParamsCycleCount: [
                    {
                        Label: "Welligent Billing Cycle ID:",
                        Name: "billID",
                        Type: "Number",
                        Required: true
                    },
                    {
                        Label: "Number of Claims in Cycle:",
                        Name: "claimCount",
                        Type: "Number",
                        Required: true
                    }
                ],
                ParamsStartEndNotify: [
                    {
                        Label: "Start Date:",
                        Name: "StartDate",
                        Type: "Date",
                        Required: true
                    },
                    {
                        Label: "End Date:",
                        Name: "EndDate",
                        Type: "Date",
                        Required: true
                    },
                    {
                        Label: "Auto-Send Emails to Providers of Issues?",
                        Name: "Notify",
                        Type: "Select",
                        Required: true,
                        Options: [
                            {
                                text: "No",
                                value: false
                            },
                            {
                                text: "Yes",
                                value: true
                            }
                        ]
                    },
                ],
                ParamsStartEndLocation: [
                    {
                        Label: "Start Date:",
                        Name: "StartDate",
                        Type: "Date",
                        Required: true
                    },
                    {
                        Label: "End Date:",
                        Name: "EndDate",
                        Type: "Date",
                        Required: true
                    },
                    {
                        Label: "Location(s):",
                        Name: "Location",
                        Type: "Select",
                        Options: [
                            {
                                text: "Wraparound",
                                value: "Wraparound"
                            },
                            {
                                text: "Residential",
                                value: "Residential"
                            },
                            {
                                text: "Outpatient",
                                value: "Outpatient"
                            }
                        ]
                    }
                ],
                ParamsTargetDateSubProgramIDs: [
                    {
                        Label: "Target Date:",
                        Name: "TargetDate",
                        Type: "Date",
                        Required: true
                    },
                    {
                        Label: "Location(s):",
                        Name: "Programs",
                        Type: "MultiSelect",
                        Options: [
                            {
                                text: "Wraparound",
                                value: "12E83DA9-E178-4DB2-9077-6097F277443A"
                            },
                            {
                                text: "Residential",
                                value: "41870532-AB7A-445B-A690-733150960EBF"
                            },
                            {
                                text: "Desert Outpatient",
                                value: "28CD87C1-85B2-4B3B-B348-8ED0688B7F15"
                            },
                            {
                                text: "Midcounty Outpatient",
                                value: "CBC3D45D-E45C-4BD5-887F-5AB87F154038"
                            }
                        ]
                    }
                ],
                ParamsSubProgramID: [
                    {
                        Label: "Program",
                        Name: "SubProgramID",
                        Type: "HiddenConstant",
                        Value: "41870532-AB7A-445B-A690-733150960EBF",
                        DisplayValue: "Inland STRTP"
                    }
                ],
                ParamsLocationMultiSelectStartEnd: [
                    {
                        Label: "Service Location(s) Listed on Progress Note:",
                        Name: "LocationNames",
                        Type: "MultiSelect",
                        Required: true,
                        Options: [
                            {
                                text: "WRAPAROUND: Riverside Wrap",
                                value: "Riverside"
                            },
                            {
                                text: "WRAPAROUND: Desert Wrap",
                                value: "Riverside-Desert"
                            },
                            {
                                text: "RESIDENTIAL: Beazer",
                                value: "Riverside-STRTP-Beazer"
                            },
                            {
                                text: "RESIDENTIAL: Forecast",
                                value: "Riverside-STRTP-Forecast"
                            },
                            {
                                text: "RESIDENTIAL: Lyon",
                                value: "Riverside-STRTP-Lyon"
                            },
                            {
                                text: "RESIDENTIAL: Richmond",
                                value: "Riverside-STRTP-Richmond"
                            },
                            {
                                text: "OUTPATIENT: Desert Outpatient Clinic",
                                value: "Riverside- Desert Outpatient"
                            },
                        ]
                    },
                    {
                        Label: "Start Date:",
                        Name: "StartDate",
                        Type: "Date",
                        Required: true
                    },
                    {
                        Label: "End Date:",
                        Name: "EndDate",
                        Type: "Date",
                        Required: true
                    }
                ],
                ParamsCensusApprovalHistory: [
                    {
                        Label: "Start Date:",
                        Name: "StartDate",
                        Type: "Date",
                        Required: true
                    },
                    {
                        Label: "End Date:",
                        Name: "EndDate",
                        Type: "Date",
                        Required: true
                    },
                    {
                        Label: "Data Selection",
                        Name: "DataSelection",
                        Type: "Select",
                        Required: true,
                        Options: [
                            {
                                text: "Any Match",
                                value: "Any Match"
                            },
                            {
                                text: "Exact Match",
                                value: "Exact Match"
                            },
                        ]
                    },
                    {
                        Label: "Core Program",
                        Name: "CoreProgramID",
                        Type: "Select",
                        Required: true,
                        Options: [
                            {
                                text: "Foster",
                                value: "2E25AACC-64C2-4283-976D-DEA8B5052FA6"
                            },
                            {
                                text: "Residential",
                                value: "15A44C4E-9500-4706-B363-F0B7670B862C"
                            },
                            {
                                text: "TAY",
                                value: "FF8A6236-ACBA-4DFC-8A3C-610E41AECCB9"
                            },
                            {
                                text: "Wraparound",
                                value: "4B61365B-0C2F-4CE9-8BB0-D79584938614"
                            },
                        ]
                    },
                ],
            };
        },
        methods: {
            CheckPermission(permissionName) {
                return this.$store.getters["User.PrivateProfile.CheckPermission"](permissionName);
            },
        },
        computed: {

        },
        components: {
            ReportDownloader
        },
        mounted() {

        }
    }
</script>

<style scoped>
</style>